import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import { track, Events } from '../lib/analytics'

export default function JoinGroup() {
  const { inviteCode } = useParams()
  const { user } = useAuth()
  const { refreshGroups } = useGroup()
  const navigate = useNavigate()
  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    async function loadGroup() {
      const { data: g, error: gErr } = await supabase
        .from('groups')
        .select('*, profiles!groups_host_id_fkey(display_name)')
        .eq('invite_code', inviteCode)
        .single()

      if (gErr || !g) {
        setError('Invite link not found or expired.')
        setLoading(false)
        return
      }
      setGroup(g)

      const { data: m } = await supabase
        .from('group_members')
        .select('*, profiles(display_name)')
        .eq('group_id', g.id)

      setMembers(m || [])
      setLoading(false)
    }
    loadGroup()
  }, [inviteCode])

  async function handleJoin() {
    setJoining(true)
    try {
      // Check if already a member
      const { data: existing } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', group.id)
        .eq('user_id', user.id)
        .single()

      if (!existing) {
        await supabase.from('group_members').insert({
          group_id: group.id,
          user_id: user.id,
          role: 'member',
        })
        track(Events.GroupJoined)
      }
      await refreshGroups(group.id)
      navigate('/')
    } catch (err) {
      setError(err.message)
      setJoining(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={() => navigate('/login')} className="fp-btn fp-btn-primary max-w-xs">
          Go to login
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="text-5xl mb-4">🎬</div>
        <h1 className="text-xl font-bold text-white">{group.name}</h1>
        <p className="text-sm text-[var(--fp-text-muted)] mt-1">
          {group.profiles?.display_name || 'Someone'} invited you to join
        </p>

        <div className="flex justify-center mt-5 mb-2">
          {members.slice(0, 4).map((m, i) => (
            <div
              key={m.id}
              className="w-9 h-9 rounded-full border-2 border-[var(--fp-bg)] flex items-center justify-center text-sm font-semibold text-white -ml-2 first:ml-0"
              style={{ background: ['#e94560', '#0f3460', '#533483', '#e94560'][i % 4] }}
            >
              {m.profiles?.display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          ))}
          {members.length > 4 && (
            <div className="w-9 h-9 rounded-full border-2 border-[var(--fp-bg)] flex items-center justify-center text-xs font-semibold text-white -ml-2 bg-[var(--fp-border-accent)]">
              +{members.length - 4}
            </div>
          )}
        </div>
        <p className="text-xs text-[var(--fp-text-muted)] mb-6">
          {members.length} member{members.length !== 1 ? 's' : ''} already in this group
        </p>

        <button onClick={handleJoin} className="fp-btn fp-btn-primary" disabled={joining}>
          {joining ? 'Joining...' : 'Join group'}
        </button>
      </div>
    </div>
  )
}
